import { useCallback, useEffect } from 'react';
import { AnalyticsContext, AnalyticsEventName, NudgeAnalyticsContext } from './constants';
import { useLazyGetItemAnalyticsModelByItemPksQuery } from '@/redux/api/client/itemAnalyticsModel';
import { ConvertItemAnalyticsModel, ConvertItemAnalyticsModelBase } from './analyticsUtils';
import { trackEvent } from './utils';
import { ItemAnalyticsModel, ItemListAnalyticsModel } from '@/@types/client-api';

const LAST_SELECT_ITEM_CONTEXT_KEY = 'last_select_item_context';
const LAST_SELECT_ITEM_MAX_AGE_MS = 5 * 60 * 1000;

type LastSelectItemContext = {
  itemId?: string;
  listId?: string;
  listName?: string;
  index?: number;
  timestamp?: number;
};

export const useTrackViewItem = (contentCode?: string) => {
  const [fetchItems] = useLazyGetItemAnalyticsModelByItemPksQuery();

  useEffect(() => {
    if (!contentCode) return;
    const fetchAndTrack = async () => {
      const { data } = await fetchItems({
        itemPks: [contentCode],
        populatePrice: true,
        limitPriceCalls: true
      });

      if (data && data.length > 0) {
        const item = data[0];
        let itemListAnalyticsModel: ItemListAnalyticsModel | undefined;
        let itemIndex: number = 0;

        try {
          const raw = sessionStorage.getItem(LAST_SELECT_ITEM_CONTEXT_KEY);
          if (raw) {
            const parsed = JSON.parse(raw) as LastSelectItemContext;
            const isFresh =
              typeof parsed.timestamp === 'number' &&
              Date.now() - parsed.timestamp <= LAST_SELECT_ITEM_MAX_AGE_MS;
            const isSameItem = !!parsed.itemId && parsed.itemId === item.id;

            if (isFresh && isSameItem) {
              itemListAnalyticsModel = {
                listId: parsed.listId,
                listName: parsed.listName
              };
              itemIndex = typeof parsed.index === 'number' && parsed.index >= 0 ? parsed.index : 0;
            }
          }
        } catch {
          // no-op
        }

        trackEvent({
          eventName: AnalyticsEventName.ViewItem,
          parameters: {
            ecommerce: ConvertItemAnalyticsModel(item, undefined, itemListAnalyticsModel, itemIndex)
          }
        });
      }
    };

    fetchAndTrack();
  }, [contentCode, fetchItems]);
};

const trackContentsEvent = (
  eventName: AnalyticsEventName,
  context?: AnalyticsContext,
  itemAnalyticsModel?: ItemAnalyticsModel
) => {
  if (context && itemAnalyticsModel) {
    trackEvent({
      eventName,
      parameters: {
        context: context || '',
        items: [ConvertItemAnalyticsModelBase(itemAnalyticsModel)]
      }
    });
  }
};

export const trackPdpDownloadContentsEvent = (
  context?: AnalyticsContext,
  itemAnalyticsModel?: ItemAnalyticsModel
) => trackContentsEvent(AnalyticsEventName.DownloadContents, context, itemAnalyticsModel);

export const trackPubUpdateInfoContentsEvent = (
  context?: AnalyticsContext,
  itemAnalyticsModel?: ItemAnalyticsModel
) => trackContentsEvent(AnalyticsEventName.PubUpdateInfo, context, itemAnalyticsModel);

export const trackSearchContentsContentsEvent = (
  context?: AnalyticsContext,
  itemAnalyticsModel?: ItemAnalyticsModel
) => trackContentsEvent(AnalyticsEventName.SearchContents, context, itemAnalyticsModel);
export const useTrackViewCrossLink = () => {
  return useCallback((context?: AnalyticsContext) => {
    trackEvent({
      eventName: AnalyticsEventName.ViewCrossLink,
      parameters: {
        context: context || ''
      }
    });
  }, []);
};

export const trackDownloadScheduleEvent = () => {
  trackEvent({
    eventName: AnalyticsEventName.DownloadSchedule,
    parameters: {}
  });
};

export const trackMemSuitePdpEvent = (name?: string) => {
  if (name) {
    trackEvent({
      eventName: AnalyticsEventName.MemSuitePdp,
      parameters: {
        name
      }
    });
  }
};

export const trackFollowEvent = (
  context?: NudgeAnalyticsContext,
  itemAnalyticsModel?: ItemAnalyticsModel
) => {
  const item = ConvertItemAnalyticsModelBase(itemAnalyticsModel);

  if (context && itemAnalyticsModel) {
    trackEvent({
      eventName: AnalyticsEventName.Follow,
      parameters: {
        context: context || '',
        ...(item && { items: [item] })
      }
    });
  }
};
